import { EncChannel, EncFilterField, Encoding, FilterType, VariableType } from '@mode-vegas/vegas';
import { SelectedChartService } from '@mode/charts/contract';
import { FilterModalService } from '@mode/charts/feature-encoding';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { AnalyticsService } from '@mode/shared/util-segment';
import { CompiledField, CompiledFormula, Switch } from '@mode/shared/util-switch';
import { ToastService } from '@mode/shared/util-toast';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MixableBaseController } from '../../../../common/component';
import { ChartObservables } from '../../../../types/viz-designer.types';
import { DropZone } from './builder-helpers';

class FilterEncodingBuilderController extends MixableBaseController {
  static $inject = ['ToastService', 'AnalyticsService', 'FilterModalService'];
  observables: ChartObservables;
  model: FlamingoTypes.ModeModel;
  encoding: Encoding;
  queryToken: string;
  queryRunToken: string;
  chartService: SelectedChartService;
  appendToSelector: string;
  filterZone: DropZone;
  disableAggregate: boolean;
  destroyed$ = new Subject<void>();

  constructor(
    private alertService: ToastService,
    private analyticsService: AnalyticsService,
    private filterModalService: FilterModalService
  ) {
    super();
  }

  $onInit() {
    this.appendToSelector = this.chartService.readonly ? '.report-view-container' : 'body';

    this.observables.encoding$.pipe(takeUntil(this.destroyed$)).subscribe((encoding) => (this.encoding = encoding));

    this.observables.queryToken$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((queryToken) => (this.queryToken = queryToken));

    this.observables.queryRunToken$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((queryRunToken) => (this.queryRunToken = queryRunToken));

    this.filterZone = new DropZone(
      'Filter',
      'filter',
      this.chartService,
      undefined,
      this.analyticsService,
      this.observables.chartType$,
      this.destroyed$,
      undefined,
      undefined,
      (field: EncFilterField) => this.removeField(field)
    );
  }

  $onDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // Filter Field Events
  fieldDropped($channel: EncChannel) {
    this.filterZone.hoverZone = null; // TODO: somehow do this in the DropZone class?
    const dragInfo = this.chartService.dragField;

    if (dragInfo != null && dragInfo.field != null) {
      if (this.disableAggregate && dragInfo.field.isNonScalarCalc) {
        this.alertService.error('Aggregate/Analytic filters are not supported on this visualization type');
      } else if (!this.disableAggregate && dragInfo.field.isAnalyticCalc) {
        this.alertService.error('Analytic filters are not yet supported');
      } else {
        this.handleFieldAdded(dragInfo.field);
      }
    }

    this.chartService.endDrag();
  }

  handleFieldAdded(formula: CompiledFormula, onFinalize = undefined) {
    const view = Switch.Views.find(this.model, this.queryToken);
    this.filterModalService.addFilter(
      this.queryRunToken,
      formula,
      this.encoding,
      view,
      (filterField: EncFilterField, field: CompiledField) => {
        const newEncoding = EncodingHelpers.addFilterToChartEncoding(this.encoding, filterField, field);
        this.chartService.dispatchEncodingUpdate(newEncoding);
        if (onFinalize) {
          onFinalize();
        }
      },
      () => {
        if (onFinalize) {
          onFinalize();
        }
      }
    );
  }

  onAttrSelected($event) {
    const { attr, onFinalize } = $event;
    this.handleFieldAdded(attr, onFinalize);
  }

  editFilter(encFilterField: EncFilterField, oldFilter: EncFilterField) {
    const view = Switch.Views.find(this.model, this.queryToken);

    encFilterField = { ...encFilterField };
    encFilterField.id ??= oldFilter?.id;

    this.filterModalService.editFilter(
      this.queryRunToken,
      encFilterField,
      this.encoding,
      view,
      (filterField: EncFilterField, field: CompiledField) => {
        // Vegas' addFilter is actually upsert
        const result = EncodingHelpers.addFilterToChartEncoding(this.encoding, filterField, field);
        this.chartService.dispatchEncodingUpdate(result);
      }
    );
  }

  varChanged(field: EncFilterField, varType: VariableType) {
    let newFilterField: EncFilterField;

    if (varType === VariableType.DISCRETE) {
      switch (field.filterType) {
        case FilterType.Continuous:
        case FilterType.ContinuousDate:
        case FilterType.RelativeDate: {
          newFilterField = EncodingHelpers.modifyFilterFieldContinuousToDiscrete(field);
          break;
        }
      }
    } else {
      // @todo allow top by field conversions
      if (field.filterType === FilterType.Discrete) {
        newFilterField = EncodingHelpers.modifyFilterFieldDiscreteToContinuous(
          Switch.Views.find(this.model, this.queryToken),
          field,
          true
        );
      }
    }

    if (newFilterField) {
      this.editFilter(newFilterField, field);
    }
  }

  removeField($field: EncFilterField) {
    const encResult = EncodingHelpers.removeFilterFromEncoding(this.encoding, $field);
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  updateChartType(type: ChartTypes.ChartType) {
    this.chartService.updateChartType(type);
  }
}

export const FilterEncodingBuilderComponent: angular.IComponentOptions = {
  bindings: {
    observables: '<',
    model: '<',
    encoding: '<',
    chartService: '<',
    disableAggregate: '<',
  },
  template: `
    <div class="card-block" ng-class="{'drag-over': ctrl.filterZone.hoverZone === 'filter'}"
                            ng-dragenter="ctrl.filterZone.dragEnter($event)"
                            ng-dragover="ctrl.filterZone.dragOver($event)"
                            ng-dragleave="ctrl.filterZone.dragLeave($event)"
                            ng-dragend="ctrl.filterZone.dragLeave($event)"
                            ng-drop="ctrl.fieldDropped('filter')">
      <div class="card-item">
        <div class="header">Filters</div>
        <div class="drop-area">
          <ul class="list-unstyled">
            <li ng-repeat="filter in ctrl.encoding.filter">
              <enc-filter-field
                disable-aggregate="ctrl.disableAggregate"
                on-trigger-edit="ctrl.editFilter($encFilterField, $oldFilter)"
                on-remove="ctrl.removeField($field)"
                on-var-change="ctrl.varChanged($field, $varType)"
                field="filter"
                model="ctrl.model"
                view-name="ctrl.queryToken"
                encoding="ctrl.encoding"
                on-drag-start="ctrl.filterZone.dragStart($event, $field, $valid)"
                on-drag-cancel="ctrl.filterZone.dragEnd($event, 'filter', $field)"/>
            </li>
          </ul>
          <mode-typeahead-attrs-list
            [view]="ctrl.observables.view$ | async:this"
            [chart-type]="ctrl.observables.chartType$ | async:this"
            placeholder-text="Add fields here..."
            (attr-selected)="ctrl.onAttrSelected($event)">
          </mode-typeahead-attrs-list>
        </div>
      </div>
    </div>`,
  controller: FilterEncodingBuilderController,
  controllerAs: 'ctrl',
};
